// Selected-layer Inspector — ported from RMS ReportCardDesignerEdit.jsx.
// Per-type property editors so every property RMS can edit, we can edit too
// (same field names → identical achievable output).
import { useState } from "react"
import type { Layer } from "./types"
import type { RcAsset } from "../../../../service/report-card"

const GRADIENT_PRESETS = [
  { name: "Classic Gold", css: "linear-gradient(180deg, #b8860b 0%, #ffd700 50%, #b8860b 100%)" },
  { name: "Champagne", css: "linear-gradient(180deg, #d4a574 0%, #f6e3c5 50%, #b48c5a 100%)" },
  { name: "Bright Gold", css: "linear-gradient(135deg, #fbb034 0%, #ffdd00 100%)" },
  { name: "Silver", css: "linear-gradient(180deg, #c0c0c0 0%, #e8e8e8 50%, #a0a0a0 100%)" },
  { name: "Platinum", css: "linear-gradient(180deg, #d6d4d4 0%, #f5f3f3 50%, #c4c2c2 100%)" },
  { name: "Chrome", css: "linear-gradient(180deg, #888888 0%, #dddddd 50%, #666666 100%)" },
  { name: "Deep Black", css: "linear-gradient(180deg, #000000 0%, #333333 50%, #000000 100%)" },
  { name: "Pearl White", css: "linear-gradient(180deg, #ffffff 0%, #ececec 60%, #cfcfcf 100%)" },
  { name: "Bronze", css: "linear-gradient(180deg, #804a00 0%, #cd7f32 50%, #4a2c00 100%)" },
  { name: "Copper", css: "linear-gradient(180deg, #b87333 0%, #efb573 50%, #804b13 100%)" },
]
const SUBJECT_PALETTES = [
  { name: "Blue", dark: "#004080", medium: "#4A89C8", light: "#A2DDFD" },
  { name: "Green", dark: "#006400", medium: "#7CC889", light: "#CFFFD1" },
  { name: "Gold", dark: "#CC9900", medium: "#FFD700", light: "#FFFFA0" },
  { name: "Red", dark: "#A52A2A", medium: "#FF7F50", light: "#FFD1DC" },
  { name: "Indigo", dark: "#4B0082", medium: "#800080", light: "#DDA0DD" },
  { name: "Teal", dark: "#006666", medium: "#008080", light: "#AFEEEE" },
  { name: "Purple", dark: "#800080", medium: "#DDA0DD", light: "#E0B0FF" },
  { name: "Grey", dark: "#696969", medium: "#A9A9A9", light: "#D3D3D3" },
]
const palettePatch = (i: number) => {
  const p = SUBJECT_PALETTES[i % SUBJECT_PALETTES.length]
  return { paletteIndex: i, headerBg: p.dark, headerColor: "#ffffff", totalColor: p.dark, pillColor: p.dark, borderColor: p.dark, rowLineColor: p.medium, pillBorderColor: p.medium, bodyBg: p.light, totalBg: "#ffffff", pillBg: "#ffffff", rowColor: "#1f2937" }
}

const lbl: React.CSSProperties = { fontSize: 9, color: "#94a3b8", marginBottom: 1, display: "block", fontWeight: 600 }
const inp = "form-control form-control-sm"
const inpStyle: React.CSSProperties = { fontSize: 11, padding: "2px 6px" }

type Props = {
  layer: Layer
  layers: Layer[]
  assets: RcAsset[]
  onChange: (patch: Partial<Layer>) => void
  onReplicate?: (proto: Layer) => void
  onDissolveFolder?: (id: string) => void
}

export default function Inspector({ layer, layers, assets, onChange, onReplicate, onDissolveFolder }: Props) {
  const L = layer as any
  const set = (patch: Record<string, any>) => onChange(patch as Partial<Layer>)
  const [showBuilder, setShowBuilder] = useState(false)
  const [gType, setGType] = useState<"linear" | "radial">("linear")
  const [gAngle, setGAngle] = useState(180)
  const [gC1, setGC1] = useState("#b8860b")
  const [gC2, setGC2] = useState("#ffd700")
  const buildGradient = () => set({ gradient: gType === "radial" ? `radial-gradient(circle, ${gC1} 0%, ${gC2} 100%)` : `linear-gradient(${gAngle}deg, ${gC1} 0%, ${gC2} 100%)` })

  // Reusable controls
  const Num = (label: string, key: string, step = 0.1) => (
    <div>
      <span style={lbl}>{label}</span>
      <input type="number" step={step} className={inp} style={inpStyle} value={L[key] ?? ""} onChange={e => set({ [key]: e.target.value === "" ? null : Number(e.target.value) })} />
    </div>
  )
  const Color = (label: string, key: string, dflt: string) => (
    <div>
      <span style={lbl}>{label}</span>
      <input type="color" className="form-control form-control-color" style={{ width: "100%", height: 26, padding: 1 }} value={L[key] && L[key] !== "transparent" ? L[key] : dflt} onChange={e => set({ [key]: e.target.value })} />
    </div>
  )
  const Check = (label: string, key: string, defaultOn = false) => (
    <label className="d-flex align-items-center gap-1" style={{ fontSize: 11, margin: 0, cursor: "pointer" }}>
      <input type="checkbox" checked={defaultOn ? L[key] !== false : !!L[key]} onChange={e => set({ [key]: e.target.checked })} />
      {label}
    </label>
  )
  const Sel = (label: string, key: string, opts: string[], dflt: string) => (
    <div>
      <span style={lbl}>{label}</span>
      <select className="form-select form-select-sm" style={inpStyle} value={L[key] || dflt} onChange={e => set({ [key]: e.target.value })}>
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )
  const Gradient = () => (
    <div>
      <span style={lbl}>Gradient text fill</span>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 3, marginBottom: 4 }}>
        {GRADIENT_PRESETS.map(g => (
          <button key={g.name} title={g.name} onClick={() => set({ gradient: g.css })}
            style={{ height: 18, borderRadius: 3, border: L.gradient === g.css ? "2px solid #2563eb" : "1px solid #cbd5e1", background: g.css, cursor: "pointer", padding: 0 }} />
        ))}
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        <button className="btn btn-sm btn-light flex-fill" style={{ fontSize: 10 }} onClick={() => setShowBuilder(s => !s)}>{showBuilder ? "Hide builder" : "Custom…"}</button>
        <button className="btn btn-sm btn-light flex-fill" style={{ fontSize: 10 }} onClick={() => set({ gradient: null })}>Solid</button>
      </div>
      {showBuilder && (
        <div style={{ marginTop: 5, padding: 6, border: "1px solid #e2e8f0", borderRadius: 4, background: "#f8fafc" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: 4 }}>
            <select className="form-select form-select-sm" style={inpStyle} value={gType} onChange={e => setGType(e.target.value as any)}>
              <option value="linear">Linear</option><option value="radial">Radial</option>
            </select>
            {gType === "linear" && <input type="number" className={inp} style={inpStyle} value={gAngle} onChange={e => setGAngle(Number(e.target.value))} title="Angle°" />}
          </div>
          <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
            <input type="color" className="form-control form-control-color" style={{ flex: 1, height: 26, padding: 1 }} value={gC1} onChange={e => setGC1(e.target.value)} />
            <input type="color" className="form-control form-control-color" style={{ flex: 1, height: 26, padding: 1 }} value={gC2} onChange={e => setGC2(e.target.value)} />
          </div>
          <div style={{ height: 14, borderRadius: 3, marginBottom: 4, background: gType === "radial" ? `radial-gradient(circle, ${gC1}, ${gC2})` : `linear-gradient(${gAngle}deg, ${gC1}, ${gC2})` }} />
          <button className="btn btn-sm btn-primary w-100" style={{ fontSize: 10 }} onClick={buildGradient}>Apply gradient</button>
        </div>
      )}
    </div>
  )

  const t = layer.type
  const grid2: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }
  const grid3: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 6 }

  // ── Folder ──
  if (t === "folder") {
    const members = layers.filter(l => (l as any).groupId === layer.id)
    return (
      <div style={{ padding: 10 }}>
        <div style={{ background: "#eef2ff", border: "1px solid #dfe6ff", borderRadius: 5, padding: 8, fontSize: 11, color: "#2e3192", marginBottom: 8 }}>
          📁 Folder · {members.length} layer{members.length === 1 ? "" : "s"} — organises layers in the list; not drawn on the canvas.
        </div>
        <span style={lbl}>Folder name</span>
        <input className={inp} style={inpStyle} value={L.name || ""} onChange={e => set({ name: e.target.value })} />
        {onDissolveFolder && <button className="btn btn-sm btn-outline-danger w-100 mt-2" style={{ fontSize: 11 }} onClick={() => onDissolveFolder(layer.id)}>✂ Dissolve folder</button>}
        <ul style={{ marginTop: 8, padding: "4px 6px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 4, fontSize: 10, listStyle: "none" }}>
          {members.map(m => <li key={m.id} style={{ display: "flex", justifyContent: "space-between", padding: "1px 0" }}><span>{(m as any).isFolderAnchor && "⚓ "}{(m as any).name}</span><span style={{ color: "#94a3b8", fontFamily: "monospace" }}>{m.type.replace("rms_", "")}</span></li>)}
        </ul>
      </div>
    )
  }

  return (
    <div style={{ padding: 10, overflowY: "auto" }}>
      {/* Name */}
      <div style={{ marginBottom: 6 }}>
        <span style={lbl}>Name</span>
        <input className={inp} style={inpStyle} value={L.name || ""} onChange={e => set({ name: e.target.value })} disabled={layer._inherited} />
      </div>

      {/* Position + size (%) */}
      <div style={gr2(grid2)}>
        {Num("Left %", "leftPct")}{Num("Top %", "topPct")}
        {Num("Width %", "widthPct")}{Num("Height %", "heightPct")}
      </div>
      <div style={grid2}>{Num("Rotation°", "rotation", 1)}{Num("Opacity", "opacity", 0.05)}</div>

      {!layer._inherited && (
        <>
          <hr style={{ margin: "6px 0" }} />

          {/* ── TEXT ── */}
          {t === "text" && (<>
            <div style={{ marginBottom: 6 }}><span style={lbl}>Token / text</span>
              <textarea className={inp} style={{ ...inpStyle, fontFamily: "monospace" }} rows={3} value={L.text ?? L.content ?? ""} onChange={e => set({ text: e.target.value, content: e.target.value })} /></div>
            <div style={{ marginBottom: 6 }}><span style={lbl}>Sample (preview only)</span>
              <textarea className={inp} style={inpStyle} rows={2} value={L.sample ?? ""} onChange={e => set({ sample: e.target.value })} /></div>
            <div style={grid3}>{Num("Font pt", "fontSize")}{Sel("Weight", "fontWeight", ["400","500","600","700","800","900"], "700")}{Sel("Align", "textAlign", ["left","center","right"], "left")}</div>
            <div style={{ display: "flex", gap: 12, marginBottom: 6 }}>{Check("Auto-shrink", "autoFit")}{Check("Auto-wrap", "autoWrap", true)}</div>
            <div style={grid2}>{Color("Text colour", "color", "#1f2937")}{Color("Background", "bg", "#ffffff")}</div>
            <button className="btn btn-sm btn-light w-100 mb-2" style={{ fontSize: 10 }} onClick={() => set({ bg: "transparent" })}>Clear background</button>
            <div style={grid3}>{Num("Border mm", "borderWidth")}{Num("Radius mm", "borderRadius")}{Color("Border", "borderColor", "#1c1917")}</div>
            <Gradient />
          </>)}

          {/* ── IMAGE ── */}
          {t === "image" && (<>
            {Sel("Fit", "fit", ["contain", "cover", "fill"], "cover")}
            <div style={{ marginTop: 6 }}><span style={lbl}>Replace asset</span>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 3, maxHeight: 140, overflowY: "auto" }}>
                {assets.map(a => <img key={a.key} src={a.url} title={a.name} onClick={() => set({ src: a.key })} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", border: L.src === a.key ? "2px solid #2563eb" : "1px solid #e2e8f0", borderRadius: 3, cursor: "pointer" }} />)}
              </div>
            </div>
          </>)}

          {/* ── PHOTO ── */}
          {t === "rms_photo" && <div style={grid3}>{Num("Border mm", "borderWidth")}{Num("Radius mm", "borderRadius")}{Color("Border", "borderColor", "#2e3192")}</div>}

          {/* ── CAMPUS LOGO ── */}
          {t === "rms_campus_logo" && (<>
            <div style={grid3}>{Num("Border mm", "borderWidth")}{Num("Radius mm", "borderRadius")}{Color("Border", "borderColor", "#3b82f6")}</div>
            {Sel("Fit mode", "objectFit", ["contain","cover","fill"], "contain")}
          </>)}

          {/* ── CAMPUS BANNER ── */}
          {t === "rms_campus_header" && (<>
            {Sel("Banner source", "bannerMode", ["auto","image","text","per-campus"], "auto")}
            <div style={{ marginTop: 6 }}><span style={lbl}>Banner text (when text mode)</span>
              <input className={inp} style={inpStyle} value={L.bannerText || ""} onChange={e => set({ bannerText: e.target.value })} /></div>
            <div style={{ marginTop: 6 }}><span style={lbl}>Banner image (when image mode)</span>
              <input className={inp} style={inpStyle} value={L.bannerImageUrl || ""} onChange={e => set({ bannerImageUrl: e.target.value })} placeholder="pick below or paste a key/URL" /></div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 3, maxHeight: 120, overflowY: "auto", marginTop: 4, marginBottom: 6 }}>
              {assets.map(a => <img key={a.key} src={a.url} title={a.name} onClick={() => set({ bannerImageUrl: a.key, bannerMode: "image" })} style={{ width: "100%", aspectRatio: "1", objectFit: "contain", background: "#f1f5f9", border: L.bannerImageUrl === a.key ? "2px solid #2563eb" : "1px solid #e2e8f0", borderRadius: 3, cursor: "pointer" }} />)}
            </div>
            <div style={grid3}>{Num("Font pt", "fontSize")}{Sel("Weight", "fontWeight", ["400","600","700","800","900"], "900")}{Sel("Align", "textAlign", ["left","center","right"], "center")}</div>
            {Check("Auto-shrink", "autoFit")}
            <div style={{ marginTop: 6 }}>{Color("Text colour", "color", "#2e3192")}</div>
            <div style={{ marginTop: 6 }}><Gradient /></div>
          </>)}

          {/* ── ATTENDANCE BAR ── */}
          {t === "rms_attendance_bar" && (<>
            <div style={{ marginBottom: 6 }}><span style={lbl}>Label text</span><input className={inp} style={inpStyle} value={L.labelText || ""} onChange={e => set({ labelText: e.target.value })} /></div>
            <div style={{ display: "flex", gap: 12, marginBottom: 6 }}>{Check("Show label", "showLabel")}{Check("Show ratio", "showRatio")}</div>
            <div style={grid3}>{Num("Label pt", "labelFontSize")}{Num("Radius mm", "borderRadius")}{Color("Label", "labelColor", "#2e3192")}</div>
            <div style={grid3}>{Color("Filled", "barColor", "#4ade80")}{Color("Track", "trackColor", "#e5e7eb")}{Color("Border", "borderColor", "#2e3192")}</div>
          </>)}

          {/* ── GRADE BADGE ── */}
          {t === "rms_grade_badge" && (<>
            <div style={grid2}>{Sel("Preview grade", "sampleGrade", ["A*","A+","A","B","C","D","E","F","U","AB"], "A")}<div style={{ alignSelf: "end" }}>{Check("Star accents", "showStarAccent")}</div></div>
            <div style={{ fontSize: 9.5, color: "#94a3b8", marginBottom: 6 }}>A* / A+ blue, A green, B sky, C blue, D blue, E blue, F / U / AB red.</div>
            <div style={grid2}>{Color("Pass outer", "passOuterColor", "#0ea5e9")}{Color("Pass inner", "passInnerColor", "#bae6fd")}{Color("Pass letter", "passLetterColor", "#eef2ff")}{Color("Pass stroke", "passLetterStroke", "#1e3a8a")}</div>
          </>)}

          {/* ── CONGRATS ── */}
          {t === "rms_congrats" && (<>
            <div style={grid3}>{Sel("Preview grade", "sampleGrade", ["A*","A+","A","B","C","D","E","F","U","AB"], "A*")}
              <div><span style={lbl}>Preview name</span><input className={inp} style={inpStyle} value={L.sampleName || ""} onChange={e => set({ sampleName: e.target.value })} /></div>
              <div style={{ alignSelf: "end" }}>{Check("Italic", "italic", true)}</div></div>
            <div style={grid3}>{Num("Font pt", "fontSize")}{Sel("Weight", "fontWeight", ["400","600","700","800","900"], "800")}{Sel("Align", "textAlign", ["left","center","right"], "center")}</div>
            <div style={{ marginBottom: 6 }}>{Color("Text colour", "color", "#2e3192")}</div>
            <Gradient />
          </>)}

          {/* ── MARKS TABLE ── */}
          {t === "rms_marks_table" && (<>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 6 }}>{Check("Subject", "showSubject")}{Check("Max", "showMax")}{Check("Obtained", "showObtained")}{Check("Percent", "showPercent")}{Check("Grade", "showGrade")}</div>
            <div style={grid3}>{Num("Font pt", "fontSize")}{Color("Header bg", "headerBg", "#3d5ee1")}{Color("Header text", "headerColor", "#2e3192")}{Color("Banded row", "bandColor", "#eef2ff")}{Color("Border", "borderColor", "#2e3192")}</div>
          </>)}

          {/* ── SUBJECT TABLE ── */}
          {t === "rms_subject_table" && (<>
            {onReplicate && <button className="btn btn-sm btn-primary w-100 mb-2" style={{ fontSize: 11 }} onClick={() => onReplicate(layer)}>🔁 Replicate styling to all subjects</button>}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 6 }}>{Check("Header", "showHeader", true)}{Check("Subcats", "showSubcats", true)}{Check("Total", "showTotal", true)}{Check("Grade", "showGrade", true)}{Check("Grade badge", "showGradeBadge", true)}{Check("Grade only", "gradeOnly")}{Check("Pill cells", "pillCells")}</div>
            {/* Palette cycler */}
            <span style={lbl}>Colour palette</span>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(8,1fr)", gap: 3, marginBottom: 6 }}>
              {SUBJECT_PALETTES.map((p, i) => <button key={p.name} title={p.name} onClick={() => set(palettePatch(i))} style={{ height: 18, borderRadius: 3, border: L.paletteIndex === i ? "2px solid #2563eb" : "1px solid #cbd5e1", background: p.dark, cursor: "pointer", padding: 0 }} />)}
            </div>
            <div style={grid2}>{Num("Font pt", "fontSize")}{Sel("Header case", "headerCase", ["none","upper","lower","title"], "upper")}</div>
            <div style={grid3}>{Color("Header bg", "headerBg", "#0ea5e9")}{Color("Header text", "headerColor", "#eef2ff")}{Color("Body bg", "bodyBg", "#ffffff")}</div>
            <div style={grid3}>{Color("Row text", "rowColor", "#1f2937")}{Color("Row line", "rowLineColor", "#cbd5e1")}{Color("Total bg", "totalBg", "#eef2ff")}</div>
            <div style={grid3}>{Color("Total text", "totalColor", "#2e3192")}{Color("Border", "borderColor", "#cbd5e1")}{Num("Border mm", "borderWidth")}</div>
            {L.variant === "styled" && (<>
              <hr style={{ margin: "6px 0" }} /><div style={{ fontSize: 9.5, fontWeight: 700, color: "#0f766e", marginBottom: 4 }}>STYLED CARD</div>
              <div style={grid2}>{Color("Accent", "accentColor", "#0d6e6e")}{Color("Accent dark", "accentDarkColor", "#054b4b")}</div>
              <div style={grid3}>{Num("Badge mm", "badgeSizeMm")}{Sel("Badge style", "badgeStyle", ["metallic","flat","outline"], "metallic")}{Num("Total bar %", "totalBarWidthPct")}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>{Check("Progress bars", "progressBars", true)}{Check("Badge obtained", "badgeShowObtained", true)}{Check("Badge max", "badgeShowMax")}{Check("Badge grade", "badgeShowGrade", true)}{Check("Total max", "totalShowMax", true)}</div>
            </>)}
          </>)}
        </>
      )}
    </div>
  )
}

// tiny helper so the position grid reuses the same style object literal
function gr2(s: React.CSSProperties) { return s }
